﻿import $ from "jquery";

// =========================================================
// Chunk Upload (Admin) - ES5 / older TypeScript compatible
// =========================================================
// - No Promise / async / await
// - No Set / Map
// - No ?? or template literals
// - Uses $.ajax for init/status/complete
// - Uses XMLHttpRequest for chunk upload + real progress
//
// Endpoints used (default):
//   POST   /admin/uploads/init
//   GET    /admin/uploads/{uploadId}/status
//   POST   /admin/uploads/{uploadId}/chunk
//   POST   /admin/uploads/{uploadId}/complete
//
// Server 'complete' should return JSON like:
//   { ok: true, url: "/data/uploads/x.pdf", fileName: "x.pdf", originalFileName?: "Original.pdf", size: 123 }

export type ChunkUploadInitResponse = {
    uploadId: string;
    chunkSize: number;
    totalChunks: number;
};

export type ChunkUploadStatusResponse = {
    uploadId: string;
    totalChunks: number;
    chunkSize: number;
    uploadedIndexes: number[];
};

export type ChunkUploadCompleteResponse = {
    ok: boolean;
    url: string;
    fileName: string;
    originalFileName?: string;
    size?: number;
};

export type ChunkUploadOptions = {
    // endpoints
    initUrl?: string; // default /admin/uploads/init
    statusUrlTemplate?: string; // default /admin/uploads/{uploadId}/status
    chunkUrlTemplate?: string; // default /admin/uploads/{uploadId}/chunk
    completeUrlTemplate?: string; // default /admin/uploads/{uploadId}/complete

    // UI wiring (optional)
    progressBarSelector?: string; // default #uploadProgressBar
    statusSelector?: string; // default #uploadStatus

    // chunk size override (server decides; this is only fallback)
    fallbackChunkSizeBytes?: number; // default 10MB
};

export type ChunkUploadCallbacks = {
    onProgress?: (percent0to100: number) => void;
    onStatus?: (text: string) => void;
    onDone: (result: ChunkUploadCompleteResponse) => void;
    onError: (message: string) => void;
};

function clampPercent(p: number): number {
    if (isNaN(p)) return 0;
    if (p < 0) return 0;
    if (p > 100) return 100;
    return Math.floor(p);
}

function setUiProgress(options: ChunkUploadOptions, pct: number, text?: string) {
    var percent = clampPercent(pct);

    var barSel = options.progressBarSelector || "#uploadProgressBar";
    var statusSel = options.statusSelector || "#uploadStatus";

    var $bar = $(barSel);
    if ($bar.length) {
        $bar.css("width", percent + "%");
        $bar.attr("aria-valuenow", percent.toString());
        $bar.text(text ? text : (percent + "%"));
    }

    if (text) {
        var $status = $(statusSel);
        if ($status.length) $status.text(text);
    }
}

function formatUrl(tpl: string, uploadId: string): string {
    return tpl.replace("{uploadId}", uploadId);
}

function xhrUploadChunk(url: string, formData: FormData, onProgress: (loaded: number, total: number) => void, onOk: () => void, onErr: (err: string) => void) {
    var xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);

    xhr.upload.onprogress = function (ev: any) {
        if (ev && ev.lengthComputable) {
            onProgress(ev.loaded, ev.total);
        }
    };

    xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
            onOk();
        } else {
            onErr(xhr.responseText || ("HTTP " + xhr.status));
        }
    };

    xhr.onerror = function () {
        onErr("Network error during upload.");
    };

    xhr.send(formData);
}

function indexOfNumber(arr: number[], value: number): number {
    // ES5-safe
    return arr.indexOf(value);
}

// Main entry
export function chunkUploadFileWithProgress(
    file: File,
    options: ChunkUploadOptions,
    callbacks: ChunkUploadCallbacks
) {
    var opt: ChunkUploadOptions = options || {};

    var initUrl = opt.initUrl || "/admin/uploads/init";
    var statusTpl = opt.statusUrlTemplate || "/admin/uploads/{uploadId}/status";
    var chunkTpl = opt.chunkUrlTemplate || "/admin/uploads/{uploadId}/chunk";
    var completeTpl = opt.completeUrlTemplate || "/admin/uploads/{uploadId}/complete";
    var fallbackChunkSize = opt.fallbackChunkSizeBytes || (10 * 1024 * 1024);

    function progress(p: number) {
        if (callbacks.onProgress) callbacks.onProgress(p);
        setUiProgress(opt, p);
    }

    function status(text: string) {
        if (callbacks.onStatus) callbacks.onStatus(text);
        // keep progress text unchanged unless provided
        setUiProgress(opt, NaN as any, text);
    }

    progress(0);
    status("Initializing...");

    // 1) init
    $.ajax({
        url: initUrl,
        method: "POST",
        contentType: "application/json; charset=utf-8",
        data: JSON.stringify({
            fileName: file.name,
            fileSize: file.size,
            contentType: (file as any).type
        })
    })
        .done(function (init: ChunkUploadInitResponse) {
            var uploadId = init.uploadId;
            var chunkSize = init.chunkSize || fallbackChunkSize;
            var totalChunks = init.totalChunks;

            // 2) status (resume) - best-effort
            var statusUrl = formatUrl(statusTpl, uploadId);
            $.ajax({
                url: statusUrl,
                method: "GET"
            })
                .done(function (st: ChunkUploadStatusResponse) {
                    var uploadedIndexes = (st && st.uploadedIndexes) ? st.uploadedIndexes : [];
                    uploadAllChunks(uploadId, file, chunkSize, totalChunks, uploadedIndexes);
                })
                .fail(function () {
                    uploadAllChunks(uploadId, file, chunkSize, totalChunks, []);
                });

            function uploadAllChunks(uploadId2: string, file2: File, chunkSize2: number, total2: number, uploaded: number[]) {
                var completed = uploaded.length;
                var i = 0;

                function overallPercent(currentChunkPct0to1: number): number {
                    var pct = ((completed + currentChunkPct0to1) / total2) * 100;
                    return clampPercent(pct);
                }

                function skipAlreadyUploaded() {
                    while (i < total2 && indexOfNumber(uploaded, i) !== -1) {
                        completed++;
                        progress((completed / total2) * 100);
                        i++;
                    }
                }

                function next() {
                    skipAlreadyUploaded();

                    if (i >= total2) {
                        // 3) complete
                        progress(100);
                        status("Assembling on server...");
                        var completeUrl = formatUrl(completeTpl, uploadId2);
                        $.ajax({
                            url: completeUrl,
                            method: "POST"
                        })
                            .done(function (res: ChunkUploadCompleteResponse) {
                                progress(100);
                                status("Done");
                                callbacks.onDone(res);
                            })
                            .fail(function (xhr: any) {
                                var msg = (xhr && xhr.responseText) ? xhr.responseText : "Complete failed";
                                callbacks.onError(msg);
                            });

                        return;
                    }

                    status("Uploading chunk " + (i + 1) + "/" + total2);

                    var start = i * chunkSize2;
                    var end = Math.min(file2.size, start + chunkSize2);
                    var blob = file2.slice(start, end);

                    var fd = new FormData();
                    fd.append("index", i.toString());
                    fd.append("totalChunks", total2.toString());
                    fd.append("chunk", blob, file2.name + ".part");

                    var chunkUrl = formatUrl(chunkTpl, uploadId2);

                    xhrUploadChunk(
                        chunkUrl,
                        fd,
                        function (loaded: number, totalBytes: number) {
                            var pct01 = 0;
                            if (totalBytes && totalBytes > 0) pct01 = loaded / totalBytes;
                            progress(overallPercent(pct01));
                        },
                        function () {
                            // done this chunk
                            completed++;
                            progress((completed / total2) * 100);
                            i++;
                            next();
                        },
                        function (err: string) {
                            callbacks.onError("Chunk " + i + " failed: " + err);
                        }
                    );
                }

                next();
            }
        })
        .fail(function (xhr: any) {
            var msg = (xhr && xhr.responseText) ? xhr.responseText : "Init failed";
            callbacks.onError(msg);
        });
}

// Convenience helper: run chunk upload and then fill your MVC hidden inputs
// (FileUrl, FileName, OriginalFileName) to be used in subsequent AJAX Save.
export function chunkUploadAndFillForDownloadFields(
    file: File,
    options: ChunkUploadOptions,
    callbacks: {
        onDone: (result: ChunkUploadCompleteResponse) => void;
        onError: (message: string) => void;
        onProgress?: (percent0to100: number) => void;
        onStatus?: (text: string) => void;
        // selectors for your inputs
        fileUrlSelector?: string; // default #FileUrl
        fileNameSelector?: string; // default #FileName
        originalFileNameSelector?: string; // default #OriginalFileName
    }
) {
    var fileUrlSel = callbacks.fileUrlSelector || "#FileUrl";
    var fileNameSel = callbacks.fileNameSelector || "#FileName";
    var origSel = callbacks.originalFileNameSelector || "#OriginalFileName";

    chunkUploadFileWithProgress(file, options, {
        onProgress: callbacks.onProgress,
        onStatus: callbacks.onStatus,
        onDone: function (res) {
            if (res && res.url) $(fileUrlSel).val(res.url);
            if (res && res.fileName) $(fileNameSel).val(res.fileName);

            var original = (res as any).originalFileName;
            if (original && original.length) {
                $(origSel).val(original);
            } else if (res && res.fileName) {
                // fallback: keep something sensible
                $(origSel).val(file.name);
            }

            callbacks.onDone(res);
        },
        onError: callbacks.onError
    });
}
